import {
  Button,
  breakpoints,
  Col,
  DateSeparator,
  Empty,
  Icon,
  JumpToEnd,
  onWeb,
  ScrollArea,
  type ScrollHandle,
  Tappable,
  Text,
  useTheme,
} from "@rally/ui-kit";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { useCalling, useInACall } from "../../call/calling.ts";
import { CallLine } from "../../call/line.tsx";
import { useCallMedia } from "../../call/media-session.tsx";
import { callCard } from "../../call/resource.tsx";
import { CallStrip } from "../../call/strip.tsx";
import { daySaid, oneDay } from "../../localization/day.ts";
import { formatList } from "../../localization/format.ts";
import { say } from "../../localization/say.ts";
import { calledBy } from "../../me/called.ts";
import { read } from "../../net/rally.ts";
import { goTo } from "../../router.ts";
import type { Me } from "../../session.ts";
import { coalesced } from "../activity-refresh.ts";
import { useAppsInConversation, useAppTarget } from "../app/app-target.ts";
import { InlineApps } from "../app/inline-apps.tsx";
import { useAttaching, useDropped, usePasted } from "../attachment/attaching.ts";
import { fileCard } from "../attachment/file-resource.tsx";
import { Filed } from "../attachment/filed.tsx";
import { bringIn } from "../bring-in.ts";
import { Composer } from "../composer/composer.tsx";
import { nowHeard } from "../composer/heard.ts";
import { ReplyComposer } from "../composer/reply-composer.tsx";
import { readBefore, useSaying } from "../composer/saying.ts";
import { agentFileToOffer, filesByMessage, recordWith, useFilesIn } from "../filed.ts";
import { useSearchTarget } from "../find/search-target.ts";
import { rememberLastConversation } from "../last-visited.ts";
import { FOLLOWING, goesToTheEnd, type Reading, scrolled } from "../panel/bottom.ts";
import { Head } from "../panel/head.tsx";
import { useStack } from "../panel/panels.tsx";
import { RouteLine } from "../panel/route-line.tsx";
import { roomOf, useRouting } from "../panel/routing.ts";
import { showDetails, useShowing } from "../panel/showing.ts";
import { arriving, nothingSeen, type Seen, seeing, settled } from "../person/arriving.ts";
import { Coming, Words } from "../person/coming.tsx";
import { GUTTER, WORDS_IN_COMPOSER } from "../reading.ts";
import { useBusy, useTypist } from "../run/busy.ts";
import { BusyRow } from "../run/busy-row.tsx";
import { Handed } from "../run/handed.tsx";
import { useLive } from "../run/live.ts";
import { Stopped } from "../run/stopped.tsx";
import { markRead } from "../seen.ts";
import { canonicalTypedPayload, routedMessageText } from "./message-payload.ts";
import { Said } from "./said.tsx";
import { linksSaid } from "./tags.ts";

export type { Person } from "../composer/saying.ts";
export { RESERVED_FOR_ACTIONS } from "./said.tsx";

const A_BURST_SETTLES_IN = 250;

export function Record({
  conversation,
  me,
  message,
  file,
  app,
}: {
  conversation: string;
  me: Me;
  message: string | null;
  file: string | null;
  app: string | null;
}) {
  const theme = useTheme();
  const stack = useStack();
  const roomy = !theme.isAtMost(breakpoints.phone);
  const { showing, lines, people, known, rows, sentence, names, offered, again, whoIsHere, sayIt } =
    useSaying(conversation, me);

  const reading = useRef<Reading>(FOLLOWING);
  const settling = useRef(false);
  const refresh = useMemo(() => coalesced(again), [again]);
  useEffect(
    () => () => {
      refresh.cancel();
    },
    [refresh],
  );

  const drawn = useRef(conversation);

  const opened = useRef<{ for: string; warm: boolean }>({
    for: conversation,
    warm: readBefore(conversation),
  });
  if (opened.current.for !== conversation) {
    opened.current = { for: conversation, warm: readBefore(conversation) };
    reading.current = FOLLOWING;
  }

  const seen = useRef<Seen>(nothingSeen(conversation));
  const scroll = useRef<ScrollHandle | null>(null);

  const typist = useTypist(conversation);
  const busy = useBusy(conversation);
  const calling = useCalling(conversation, null);
  const inACall = useInACall();
  const media = useCallMedia();

  const [withCamera, setWithCamera] = useState(false);

  const [clash, setClash] = useState<string | null>(null);

  const [drafting, setDrafting] = useState("");
  const [newSince, setNewSince] = useState<string | null>(null);
  const [seenAt, setSeenAt] = useState<Array<{ account: string; at: string }>>([]);
  const [replying, setReplying] = useState<{
    id: string;
    parent: string;
    kind: "message" | "app";
    said: string;
    by: string;
  } | null>(null);
  const room = useMemo(
    () => roomOf({ me: me.account, text: drafting, names, lines, replyTo: replying?.by }),
    [me.account, drafting, names, lines, replying?.by],
  );
  const routing = useRouting(conversation, room);

  useEffect(() => {
    if (showing.state === "read") rememberLastConversation(me.account, conversation);
  }, [conversation, me.account, showing.state]);

  const pressed = async (what: () => Promise<string | null>, camera = false) => {
    if (inACall && inACall.conversation !== conversation) {
      setClash(inACall.title ?? say("call.thatRoom"));
      return;
    }
    setClash(null);
    media.arriveWith(camera);
    const call = await what().catch(() => null);
    if (call) stack.open(callCard(call, conversation, me.account));
  };

  const move = async () => {
    setClash(null);
    await calling.leave().catch(() => undefined);
    await (calling.live ? calling.join() : calling.start()).catch(() => undefined);
  };

  const { files, again: filesAgain } = useFilesIn(conversation);
  const filesRefresh = useMemo(() => coalesced(filesAgain), [filesAgain]);
  useEffect(() => () => filesRefresh.cancel(), [filesRefresh]);
  const apps = useAppsInConversation(conversation);
  const attached = filesByMessage(files);
  const attaching = useAttaching(conversation);
  const seenFiles = useRef<Set<string> | null>(null);
  const agentFileArrived = useRef(false);
  const [adrift, setAdrift] = useState(false);
  usePasted(attaching.take);
  const { onNode, dropping } = useDropped(attaching.take);

  useEffect(() => {
    let alive = true;
    void read<Array<{ account: string; at: string }>>("attention.seen", { conversation })
      .then((held) => {
        if (alive) setSeenAt(held);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [conversation]);

  const pinned = useMemo(
    () =>
      rows
        .filter((row) => row.id && (row.away.pinnedForAll || row.away.pinnedForMe))
        .map((row) => ({ id: row.id as string, text: row.text })),
    [rows],
  );

  useLive((notice) => {
    if (notice.conversation !== conversation) return;
    if (notice.kind === "file.added") agentFileArrived.current = true;
    settling.current = true;
    refresh.notice();
    filesRefresh.notice();
  });
  useEffect(() => {
    if (!settling.current) return;
    const timer = setTimeout(() => {
      settling.current = false;
      void markRead(conversation);
    }, A_BURST_SETTLES_IN);
    return () => clearTimeout(timer);
  }, [conversation]);

  useEffect(() => {
    const before = seenFiles.current;
    const now = new Set(files.map((file) => file.id));
    seenFiles.current = now;
    if (!before || !agentFileArrived.current) return;
    agentFileArrived.current = false;
    const agents = new Set(offered.filter((one) => one.kind === "agent").map((one) => one.account));
    const made = agentFileToOffer(files, before, agents, stack.current !== null);
    if (made) stack.open(fileCard(made, conversation));
  }, [files, offered, stack, conversation]);

  useEffect(() => {
    if (!onWeb || typeof document === "undefined") return;
    const onKey = (event: KeyboardEvent) => {
      const at = document.activeElement as HTMLElement | null;
      if (!at || at.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(at.tagName)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const wall = document.querySelector('[data-testid="record"]');
      if (!wall) return;
      const lines = Array.from(wall.querySelectorAll<HTMLElement>('[data-testid^="message-"]'));
      if (lines.length === 0) return;
      const here = lines.findIndex((line) => line === at || line.contains(at));
      if (event.key === "j" || event.key === "k") {
        event.preventDefault();
        const next =
          event.key === "j"
            ? Math.min(lines.length - 1, here + 1)
            : here < 0
              ? lines.length - 1
              : Math.max(0, here - 1);
        lines[next]?.focus();
        lines[next]?.scrollIntoView({ block: "nearest" });
      } else if (event.key === "Escape" && here >= 0) {
        event.preventDefault();
        at.blur();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setReplying(null);
    setNewSince(null);
    let alive = true;
    void read<{ at: string | null }>("attention.position", { conversation })
      .then((held) => {
        if (alive) setNewSince(held.at ?? "");
      })
      .catch(() => {})
      .finally(() => void markRead(conversation));
    return () => {
      alive = false;
    };
  }, [conversation]);

  const detailsShowing = useShowing();
  useEffect(() => nowHeard(conversation, lines), [conversation, lines]);
  seen.current = seeing(
    seen.current,
    conversation,
    rows.map((one) => one.key),
  );
  useEffect(() => {
    if (showing.state === "read") seen.current = settled(seen.current);
  });
  const many = rows.length;
  useSearchTarget(conversation, message, file, rows, files, stack);
  useAppTarget(conversation, app, stack);
  useEffect(() => {
    const opening = drawn.current !== conversation;
    drawn.current = conversation;
    if (opening || goesToTheEnd(reading.current, many)) {
      scroll.current?.scrollToEnd({ animated: false });
    }
  }, [many, conversation]);

  return (
    <Col
      ref={onNode as never}
      style={{
        flex: 1,
        minHeight: 0,
        gap: theme.space[3],

        borderWidth: 2,
        borderColor: dropping ? theme.color.accent : "transparent",
        borderRadius: theme.radius.lg,
      }}
      testID="record-panel"
    >
      <Head
        conversation={conversation}
        me={me.account}
        people={people}
        onRoster={whoIsHere}
        onDetails={() => showDetails(!detailsShowing)}
        detailsShowing={detailsShowing}
        onCall={
          calling.available
            ? (camera) => {
                setWithCamera(camera);
                void pressed(calling.start, camera);
              }
            : undefined
        }
      />
      {pinned.length > 0 ? (
        <Tappable
          accessibilityRole="button"
          accessibilityLabel={say("chat.pinnedCount")(pinned.length)}
          testID="pinned-strip"
          onPress={() => {
            const next = pinned[(pinned.findIndex((one) => one.id === message) + 1) % pinned.length];
            if (next) goTo(`/c/${conversation}?message=${next.id}`);
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: theme.space[2],
            paddingHorizontal: GUTTER,
            paddingVertical: theme.space[2],
            borderBottomWidth: 1,
            borderBottomColor: theme.color["border.default"],
            backgroundColor: theme.color["bg.surfaceAlt"],
          }}
        >
          <Icon name="pin" size={14} color="accent" />
          <Text typeName="caption" color="text.secondary" numberOfLines={1} style={{ flex: 1 }}>
            {pinned[0]?.text}
          </Text>
          <Text typeName="caption" color="text.tertiary">
            {say("chat.pinnedCount")(pinned.length)}
          </Text>
        </Tappable>
      ) : null}
      {calling.available && calling.live ? (
        <CallStrip
          startedAt={calling.live.startedAt}
          here={calling.here}
          me={me.account}
          joined={calling.joined}
          onJoin={() => void pressed(calling.join, withCamera)}
        />
      ) : null}
      {clash ? (
        <Col
          testID="call-clash"
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: theme.space[3],
            paddingHorizontal: GUTTER,
          }}
        >
          <Text typeName="caption" color="text.secondary" style={{ flex: 1 }}>
            {say("call.inAnother")(clash)}
          </Text>
          <Button size="sm" variant="soft" testID="call-move" onPress={() => void move()}>
            {say("call.moveHere")}
          </Button>
        </Col>
      ) : null}
      <Col style={{ flex: 1, minHeight: 0 }}>
        <ScrollArea
          ref={scroll}
          onScroll={(event) => {
            const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
            reading.current = scrolled({
              at: contentOffset.y,
              content: contentSize.height,
              window: layoutMeasurement.height,
            });
            setAdrift(!reading.current.following);
          }}
          scrollEventThrottle={16}
          style={{ flex: 1, minHeight: 0 }}
          contentContainerStyle={{
            gap: theme.space[4],
            paddingHorizontal: GUTTER,
            paddingBottom: roomy ? theme.space[6] : theme.space[4],

            width: "100%",
            alignSelf: "stretch",

            paddingTop: theme.space[4],
          }}
          testID="record"
        >
          {showing.state === "loading" ? (
            <Coming />
          ) : showing.state === "refused" ? (
            <Text typeName="body" color="text.tertiary" testID="record-refused">
              {showing.why}
            </Text>
          ) : rows.length === 0 && files.length === 0 ? (
            <Empty icon="message" title={say("chat.empty")} testID="record-empty" />
          ) : (
            <Words fade={!opened.current.warm}>
              {recordWith(rows, files).map((entry, index, all) => {
                const before = index > 0 ? all[index - 1] : undefined;
                const opensADay = !before || !oneDay(entry.at, before.at);
                const mine = entry.kind === "said" && entry.row.by === me.account;
                const lastOfMine =
                  mine &&
                  !all.slice(index + 1).some((later) => later.kind === "said" && later.row.by === me.account);
                const seenByNames = lastOfMine
                  ? seenAt
                      .filter((one) => one.at > entry.at)
                      .map((one) => calledBy(known.get(one.account)))
                      .filter((name): name is string => name !== null)
                  : [];
                const opensTheNew =
                  newSince !== null &&
                  before !== undefined &&
                  entry.kind === "said" &&
                  entry.row.by !== me.account &&
                  entry.at > newSince &&
                  !(before.kind === "said" && before.row.by !== me.account && before.at > newSince);
                const grouped =
                  !opensADay &&
                  entry.kind === "said" &&
                  before?.kind === "said" &&
                  !before.row.call &&
                  !before.row.stop &&
                  !before.row.between &&
                  !entry.row.call &&
                  !entry.row.stop &&
                  !entry.row.between &&
                  before.row.by === entry.row.by &&
                  Date.parse(entry.at) - Date.parse(before.at) < 5 * 60000;
                return (
                  <Fragment key={entry.key}>
                    {opensADay ? <DateSeparator label={daySaid(entry.at)} sticky /> : null}
                    {opensTheNew ? (
                      <DateSeparator label={say("chat.newBelow")} tone="accent" testID="new-below" />
                    ) : null}
                    {entry.kind === "file" ? (
                      <Filed
                        file={entry.file}
                        conversation={conversation}
                        who={known.get(entry.file.addedBy ?? "")}
                        onDrawn={() => {
                          if (reading.current.following) scroll.current?.scrollToEnd({ animated: false });
                        }}
                      />
                    ) : entry.row.call ? (
                      <CallLine call={entry.row.call} conversation={conversation} me={me.account} />
                    ) : entry.row.stop ? (
                      <Stopped stop={entry.row.stop} by={entry.row.by} known={known} />
                    ) : entry.row.between ? (
                      <Handed by={entry.row.by} text={entry.row.text} known={known} />
                    ) : (
                      <Col style={{ gap: grouped ? 0 : theme.space[2] }}>
                        <Said
                          who={known.get(entry.row.by)}
                          text={entry.row.text}
                          at={entry.row.at}
                          from={entry.row.from}
                          arrived={arriving(seen.current, entry.row.key)}
                          highlighted={entry.row.id === message}
                          id={entry.row.id}
                          reactions={entry.row.reactions}
                          away={entry.row.away}
                          answering={entry.row.answering}
                          capabilities={entry.row.capabilities}
                          names={names}
                          files={files}
                          attachments={entry.row.id ? attached.get(entry.row.id) : undefined}
                          conversation={conversation}
                          me={me.account}
                          phone={!roomy}
                          grouped={grouped}
                          onAttachmentDrawn={() => {
                            if (reading.current.following) scroll.current?.scrollToEnd({ animated: false });
                          }}
                          onChanged={again}
                          onReply={
                            entry.row.id
                              ? () =>
                                  setReplying({
                                    id: entry.row.id as string,
                                    parent: entry.row.id as string,
                                    kind: "message",
                                    said: entry.row.text,
                                    by: entry.row.by,
                                  })
                              : undefined
                          }
                        />
                        {seenByNames.length > 0 ? (
                          <Text
                            typeName="caption"
                            color="text.tertiary"
                            style={{ alignSelf: "flex-end" }}
                            testID="seen-by"
                          >
                            {say("chat.seenBy")(formatList(seenByNames))}
                          </Text>
                        ) : null}
                        <InlineApps
                          apps={entry.row.apps ?? []}
                          onChanged={again}
                          onReply={(id, said) =>
                            setReplying({
                              id,
                              parent: entry.row.id as string,
                              kind: "app",
                              said,
                              by: entry.row.by,
                            })
                          }
                        />
                        {entry.row.id && replying?.parent === entry.row.id ? (
                          <ReplyComposer
                            conversation={conversation}
                            to={replying.id}
                            said={replying.said}
                            who={offered}
                            files={files}
                            links={linksSaid(lines.map((one) => one.text ?? ""))}
                            onAdd={async (one) => {
                              const result = await bringIn(conversation, one);
                              whoIsHere();
                              return result;
                            }}
                            onSay={(text, from, carried, submission) =>
                              sayIt(
                                text,
                                from,
                                {
                                  kind: replying.kind,
                                  id: replying.id,
                                  said: replying.said,
                                },
                                carried,
                                submission,
                              )
                            }
                            onFiles={filesAgain}
                            onClose={(target) => setReplying((one) => (one?.id === target ? null : one))}
                          />
                        ) : null}
                      </Col>
                    )}
                  </Fragment>
                );
              })}
            </Words>
          )}
        </ScrollArea>
        <JumpToEnd
          label={say("chat.latest")}
          visible={adrift}
          testID="jump-to-end"
          onPress={() => {
            scroll.current?.scrollToEnd({ animated: true });
            setAdrift(false);
          }}
        />
      </Col>
      <Col
        style={{
          paddingHorizontal: GUTTER,

          ...(roomy
            ? { paddingBottom: theme.space[5] }
            : ({
                paddingBottom: `calc(${theme.space[4]}px + env(safe-area-inset-bottom, 0px))`,
              } as object)),

          width: "100%",
          alignSelf: "stretch",
        }}
      >
        <Col style={{ marginHorizontal: WORDS_IN_COMPOSER }}>
          <BusyRow
            conversation={conversation}
            busy={busy}
            known={known}
            routing={
              routing.anything ? (
                <RouteLine
                  said={routing.said}
                  names={names}
                  phone={!roomy}
                  on={routing.on}
                  onDrop={routing.drop}
                  onAdd={routing.add}
                  onAuto={routing.setOn}
                />
              ) : null
            }
          />
        </Col>
        <Composer
          error={sentence}
          conversation={conversation}
          picked={attaching.picked}
          onPick={(files) => attaching.take(files)}
          onDrop={attaching.drop}
          onDraft={setDrafting}
          onSay={async (text, from, identify, attachedApps) => {
            reading.current = FOLLOWING;

            const said = routedMessageText(text, routing.likely);
            const carried = await attaching.ready();
            if (!said && carried.length === 0 && attachedApps.length === 0) return false;
            const files = carried.map(({ key, name, mediaType }) => ({ key, name, mediaType }));
            const submission = identify(
              canonicalTypedPayload({ conversation, text: said, files, apps: attachedApps }),
            );
            const landed = await sayIt(said, from, undefined, carried, submission, attachedApps);
            if (!landed) {
              attaching.retry(carried);
              return false;
            }
            attaching.taken(carried);
            routing.forget();
            if (carried.length > 0) filesAgain();
            return true;
          }}
          who={offered}
          files={files}
          links={linksSaid(lines.map((one) => one.text ?? ""))}
          apps={apps}
          onAdd={async (one) => {
            const said = await bringIn(conversation, one);

            whoIsHere();
            return said;
          }}
          onTyped={typist.typed}
          onStopped={typist.stopped}
        />
      </Col>
    </Col>
  );
}
